import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "../i18n";
import {
  getAdmin,
  saveConfig,
  getEvents,
  type AgendaConfig,
  type UpcomingEvent,
} from "../api";
import type { DateOverride } from "../api";

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

export function Admin() {
  const { t, lang } = useT();
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [connected, setConnected] = useState(false);
  const [config, setConfig] = useState<AgendaConfig | null>(null);
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<{ kind: "generic" } | { kind: "raw"; message: string } | null>(
    null,
  );
  const [draft, setDraft] = useState<DateOverride>({
    date: "",
    blocked: true,
  });

  useEffect(() => {
    (async () => {
      try {
        const a = await getAdmin();
        setAuthed(true);
        setConnected(a.connected);
        setConfig(a.config);
        if (a.connected) {
          try {
            setEvents(await getEvents());
          } catch {
            /* events optional */
          }
        }
      } catch (e) {
        if ((e as Error & { status?: number }).status === 401) setAuthed(false);
        else setError({ kind: "generic" });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="panel center muted">…</div>;

  if (!authed) {
    return (
      <div className="panel center">
        <span className="eyebrow">{t("adminTitle")}</span>
        <h2>{t("connectGoogle")}</h2>
        <p className="lead">{t("connectHint")}</p>
        <div className="row" style={{ justifyContent: "center" }}>
          <a className="btn primary btnlink" href="/api/admin/login">
            {t("connectGoogle")}
          </a>
        </div>
      </div>
    );
  }

  const cfg = config!;
  const setCfg = (patch: Partial<AgendaConfig>) => {
    setConfig({ ...cfg, ...patch });
    setSaved(false);
  };
  const toggleDay = (iso: number) => {
    const days = cfg.days.includes(iso)
      ? cfg.days.filter((d) => d !== iso)
      : [...cfg.days, iso].sort((a, b) => a - b);
    setCfg({ days });
  };

  const addOverride = () => {
    if (!draft.date) return;
    const next = [...cfg.overrides.filter((o) => o.date !== draft.date), draft].sort((a, b) =>
      a.date.localeCompare(b.date),
    );
    setCfg({ overrides: next });
    setDraft({ date: "", blocked: true });
  };

  const removeOverride = (date: string) => {
    setCfg({ overrides: cfg.overrides.filter((o) => o.date !== date) });
  };

  async function onSave() {
    setError(null);
    try {
      const c = await saveConfig(cfg);
      setConfig(c);
      setSaved(true);
    } catch (e) {
      const message = (e as Error).message;
      setError(message ? { kind: "raw", message } : { kind: "generic" });
    }
  }

  return (
    <div className="panel wide">
      <span className="eyebrow">{t("adminTitle")}</span>
      <div className="row" style={{ marginBottom: 12 }}>
        <Link className="btn ghost btnlink" to="/admin/leads">
          {t("viewLeads")}
        </Link>
      </div>
      <h2>{t("settings")}</h2>

      {!connected && (
        <div className="banner warn">
          {t("connectHint")}{" "}
          <a href="/api/admin/login">{t("reconnect")}</a>
        </div>
      )}

      <div className="section-label">{t("settings")}</div>
      {error && (
        <div className="banner err">
          {error.kind === "generic" ? t("genericError") : error.message}
        </div>
      )}
      <div className="grid2">
        <label>
          {t("slotDuration")}
          <input
            type="number"
            min={1}
            value={cfg.slotDurationMin}
            onChange={(e) => setCfg({ slotDurationMin: Number(e.target.value) })}
          />
        </label>
        <label>
          {t("slotInterval")}
          <input
            type="number"
            min={1}
            value={cfg.slotIntervalMin}
            onChange={(e) => setCfg({ slotIntervalMin: Number(e.target.value) })}
          />
        </label>
        <label>
          {t("windowStart")}
          <input
            type="time"
            value={cfg.windowStart}
            onChange={(e) => setCfg({ windowStart: e.target.value })}
          />
        </label>
        <label>
          {t("windowEnd")}
          <input
            type="time"
            value={cfg.windowEnd}
            onChange={(e) => setCfg({ windowEnd: e.target.value })}
          />
        </label>
        <label>
          {t("minNotice")}
          <input
            type="number"
            min={0}
            value={cfg.minNoticeMin}
            onChange={(e) => setCfg({ minNoticeMin: Number(e.target.value) })}
          />
        </label>
        <label>
          {t("buffer")}
          <input
            type="number"
            min={0}
            value={cfg.bufferMin}
            onChange={(e) => setCfg({ bufferMin: Number(e.target.value) })}
          />
        </label>
      </div>

      <fieldset className="days">
        <legend>{t("daysOfWeek")}</legend>
        {DAY_KEYS.map((k, i) => {
          const iso = i + 1;
          return (
            <label key={k} className="daychk">
              <input
                type="checkbox"
                checked={cfg.days.includes(iso)}
                onChange={() => toggleDay(iso)}
              />
              {t(k)}
            </label>
          );
        })}
      </fieldset>

      <div className="section-label">{t("overridesTitle")}</div>
      <p className="lead">{t("overridesHint")}</p>

      {cfg.overrides.length === 0 ? (
        <p className="muted">{t("noOverrides")}</p>
      ) : (
        <ul className="overrides">
          {cfg.overrides.map((o) => (
            <li key={o.date}>
              <strong>{o.date}</strong>
              <span>
                {o.blocked
                  ? t("overrideBlocked")
                  : `${t("overrideCustomHours")}: ${o.windowStart}–${o.windowEnd}`}
              </span>
              <button
                className="btn ghost"
                type="button"
                onClick={() => removeOverride(o.date)}
              >
                {t("removeOverride")}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="override-add">
        <input
          type="date"
          value={draft.date}
          onChange={(e) => setDraft({ ...draft, date: e.target.value })}
        />
        <label className="daychk">
          <input
            type="checkbox"
            checked={draft.blocked}
            onChange={(e) =>
              setDraft({
                ...draft,
                blocked: e.target.checked,
                windowStart: e.target.checked ? undefined : draft.windowStart ?? "09:00",
                windowEnd: e.target.checked ? undefined : draft.windowEnd ?? "17:00",
              })
            }
          />
          {t("overrideBlocked")}
        </label>
        {!draft.blocked && (
          <>
            <input
              type="time"
              value={draft.windowStart ?? "09:00"}
              onChange={(e) => setDraft({ ...draft, windowStart: e.target.value })}
            />
            <input
              type="time"
              value={draft.windowEnd ?? "17:00"}
              onChange={(e) => setDraft({ ...draft, windowEnd: e.target.value })}
            />
          </>
        )}
        <button className="btn primary" type="button" onClick={addOverride}>
          {t("addOverride")}
        </button>
      </div>

      <div className="row">
        <button className="btn primary" onClick={onSave} type="button">
          {t("save")}
        </button>
        {saved && <span className="ok">{t("saved")}</span>}
      </div>

      <div className="section-label">{t("upcoming")}</div>
      {events.length === 0 ? (
        <p className="muted">{t("noEvents")}</p>
      ) : (
        <ul className="events">
          {events.map((e) => (
            <li key={e.id}>
              <a href={e.htmlLink} target="_blank" rel="noreferrer">
                {new Intl.DateTimeFormat(lang === "pt" ? "pt-BR" : "en-US", {
                  dateStyle: "short",
                  timeStyle: "short",
                  timeZone: "America/Sao_Paulo",
                }).format(new Date(e.start))}{" "}
                — {e.summary}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
